import {
  AutoSave,
  Card,
  Form,
  FormField,
  FormSubmitHandler,
  IconButton,
  showNotification,
} from 'platform/components';
import {Box, Heading, HStack, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useRef} from 'react';
import {useStore} from 'react-redux';

import {
  CustomerInquiryCustomerRequest,
  useAddCustomerRequestMutation,
  useRemoveCustomerRequestMutation,
  useUpdateCustomerRequestMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';
import {JobInput, type JobInputForm} from '@dms/shared-aftersales';

import {getSafely, RequiredTestIdProps, suffixTestId} from 'shared/web';

const MAX_FIELD_LENGTH = 256;

const customerRequestRowSchema = z.object({
  request: z.string().max(MAX_FIELD_LENGTH),
  description: z.string().max(MAX_FIELD_LENGTH).nullable(),
});
type CustomerRequestRowFormType = z.infer<typeof customerRequestRowSchema>;

interface CustomerRequestsProps extends RequiredTestIdProps {
  interestId: string;
  customerRequests: CustomerInquiryCustomerRequest[];
  isDisabled?: boolean;
}

export function CustomerRequests(props: CustomerRequestsProps) {
  const [addCustomerRequest] = useAddCustomerRequestMutation();
  const [removeCustomerRequest] = useRemoveCustomerRequestMutation();

  const handleAdd: FormSubmitHandler<JobInputForm> = (values) =>
    addCustomerRequest({
      interestId: props.interestId,
      addCustomerRequestRequestBody: {request: values.job},
    })
      .unwrap()
      .catch(handleApiError);

  const handleRemove = (customerRequestId: string) => {
    removeCustomerRequest({
      interestId: props.interestId,
      customerRequestId,
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
      })
      .catch(handleApiError);
  };

  return (
    <VStack spacing={4}>
      <Heading size={4}>{i18n.t('entity.workshopInterest.labels.customerRequests')}</Heading>

      {props.customerRequests.map((customerRequest, index) => (
        <CustomerRequestRow
          key={customerRequest.id}
          customerRequest={customerRequest}
          interestId={props.interestId}
          isDisabled={props.isDisabled}
          onRemove={handleRemove}
          data-testid={suffixTestId(`customerRequest-[${index}]`, props)}
        />
      ))}

      <JobInput
        onSubmit={handleAdd}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('addCustomerRequest', props)}
      />
    </VStack>
  );
}

interface CustomerRequestRowProps extends RequiredTestIdProps {
  customerRequest: CustomerInquiryCustomerRequest;
  interestId: string;
  isDisabled?: boolean;
  onRemove: (id: string) => void;
}

function CustomerRequestRow(props: CustomerRequestRowProps) {
  const store = useStore();
  const pendingValuesRef = useRef<CustomerRequestRowFormType | null>(null);

  const cacheKey = `update-customer-request-${props.customerRequest.id}`;
  const [updateCustomerRequest] = useUpdateCustomerRequestMutation({fixedCacheKey: cacheKey});

  const isUpdateInProgress = () => {
    const state = store.getState() as {
      rtk_omneticApi?: {mutations?: Record<string, {status?: string}>};
    };
    const mutations = state.rtk_omneticApi?.mutations;
    return getSafely(mutations, cacheKey)?.status === 'pending';
  };

  const onSave = (values: CustomerRequestRowFormType) => {
    if (isUpdateInProgress()) {
      pendingValuesRef.current = {...pendingValuesRef.current, ...values};
      return;
    }

    updateCustomerRequest({
      interestId: props.interestId,
      customerRequestId: props.customerRequest.id,
      updateCustomerRequestRequestBody: {
        request: values.request,
        description: values.description,
      },
    })
      .unwrap()
      .then(() => {
        if (pendingValuesRef.current) {
          const pending = pendingValuesRef.current;
          pendingValuesRef.current = null;
          onSave(pending);
        }
      })
      .catch(handleApiError);
  };

  return (
    <Card variant="inlineWhite" data-testid={suffixTestId('card', props)}>
      <Form<CustomerRequestRowFormType>
        defaultValues={{
          request: props.customerRequest.request,
          description: props.customerRequest.description,
        }}
        experimentalZodSchema={customerRequestRowSchema}
        isDisabled={props.isDisabled}
      >
        {(control) => (
          <HStack spacing={4} align="flex-end">
            <AutoSave<CustomerRequestRowFormType> onSave={onSave} />
            <Box flex={1}>
              <FormField
                control={control}
                name="request"
                type="text"
                label={i18n.t('general.labels.request')}
                maxLength={MAX_FIELD_LENGTH}
                data-testid={suffixTestId('request', props)}
              />
            </Box>
            <Box flex={1}>
              <FormField
                control={control}
                name="description"
                type="textarea"
                label={i18n.t('general.labels.description')}
                maxLength={MAX_FIELD_LENGTH}
                minRows={1}
                isResizable
                data-testid={suffixTestId('description', props)}
              />
            </Box>
            <IconButton
              icon="content/remove_circle"
              severity="danger"
              isDisabled={props.isDisabled}
              onClick={() => props.onRemove(props.customerRequest.id)}
              data-testid={suffixTestId('remove', props)}
            />
          </HStack>
        )}
      </Form>
    </Card>
  );
}
